import moment from 'moment';
import { useSelector } from 'react-redux';

const _ = require('lodash');

const TOKEN_TYPE = {
  USER: 'user',
  FIREBASE_USER: 'firebaseUser',
  LOCAL_STORE: 'local',
  DATA_LINK: 'datalink',
  DS: 'ds',
  LEGACY: 'source',
  ACTION_SEQUENCE_STEP_RESULT: 'history',
  DATA: 'data', // the data passed into a step for example
  EPOCH: 'epoch',
  QUERYSTRING: 'querystring',
  PARAMS: 'params' // params in url path i.e. home/:orgId/things/:id
};

// in a string such as {source.something} we can apply formatDate(theresult,'format')
const formatDate = data => {
  const regEx = /formatDate\((.*?)\)/g;

  if (!data?.matchAll) {
    return data;
  }

  let returnValue = data;

  const parts = [...data.matchAll(regEx)];

  parts.forEach(part => {
    if (part.length === 2) {
      try {
        const secondaryParts = part[1].replace(/'/g, '').split(',');
        const dtParts = secondaryParts[0].split(' ');

        let isoDate = dtParts[0];
        if (dtParts[0].indexOf('-') === -1) {
          const segments = dtParts[0].split('/');
          isoDate = `${segments[2]}-${segments[1]}-${segments[0]}T${dtParts[1]}:00`;
        }

        const formattedDate = moment(isoDate.replace(/"/g, '')).format(
          secondaryParts[1].replace(/"/g, '')
        );
        returnValue = returnValue.replace(part[0], formattedDate);
      } catch (err) {
        console.log(err);
      }
    }
  });

  return returnValue;
};

export const parse = ({
  markup,
  underlyingField,
  documents,
  document,
  selections,
  local,
  user,
  firebaseUser,
  options,
  actionSequenceExecutionHistory,
  data,
  dataLinks,
  params
}) => {
  let value = markup;

  if (value?.replace) {
    // support for legacy ${source.
    value = value.replace(/\${source./g, '{');
    value = value.replace(/{source./g, '{');
    value = value.replace(/\${/g, '{'); // replace legacy dollars just in case
  }

  if (value !== undefined && value?.matchAll) {
    const tokens = [...value.matchAll(/{(.*?)}/g)];
    if (tokens.length > 0) {
      tokens.forEach(token => {
        let tokenType = token[1].split('.')[0];
        const segments = token[1].split('.');
        let path;
        if (segments.length > 1) {
          segments.splice(0, 1);
          path = segments.join('.');
        } else {
          // single {field}
          path = segments[0];
          tokenType = '';
        }

        let target;
        switch (tokenType) {
          case TOKEN_TYPE.PARAMS: {
            if (params !== undefined) {
              // params are popupated from usePage hook into data object
              target = params;
            } else {
              target = {};
            }
            break;
          }
          case TOKEN_TYPE.QUERYSTRING: {
            const params = new URLSearchParams(window.location.search);
            const queryParams = {};

            for (const [key, value] of params.entries()) {
              queryParams[key] = value;
            }

            target = queryParams;
            break;
          }
          case TOKEN_TYPE.DATA: {
            target = data;
            break;
          }
          case TOKEN_TYPE.EPOCH: {
            target = {};
            break;
          }
          case TOKEN_TYPE.ACTION_SEQUENCE_STEP_RESULT: {
            target = actionSequenceExecutionHistory;
            break;
          }
          case TOKEN_TYPE.LOCAL_STORE: {
            target = local;
            break;
          }
          case TOKEN_TYPE.USER: {
            target = user;
            break;
          }
          case TOKEN_TYPE.FIREBASE_USER: {
            target = firebaseUser;
            break;
          }
          default:
            if (
              [TOKEN_TYPE.DATA_LINK, TOKEN_TYPE.DS].some(t => t === tokenType)
            ) {
              const dataLinkValue = segments[0];

              // is this a dataLinkId or a datalink name i.e. ds.employees.field or ds.employee.field
              const dataLink = dataLinks?.find(
                d =>
                  d.id === dataLinkValue ||
                  d.name.toLowerCase() === dataLinkValue.toLowerCase() ||
                  d.plural.toLowerCase() === dataLinkValue.toLowerCase()
              );

              let dataLinkId = dataLinkValue;

              if (dataLink) {
                dataLinkId = dataLink.id;
              }

              if (selections[dataLinkId]) {
                target = documents[dataLinkId]?.documents.find(
                  d => d.id === selections[dataLinkId]?.documentId
                );
              }
              path = path.replace(`${segments[0]}.`, '');

              break;
            } else if (tokenType === TOKEN_TYPE.LEGACY) {
              path = path.replace('source.', '');
            }

            if (document && tokenType === '') {
              // if a single document has been passed in then use that
              target = document;
            }
            break;
        }

        if (tokenType === TOKEN_TYPE.EPOCH) {
          value = Date.now().toString();
        } else if (target && _.get(target, path) !== undefined) {
          if (_.get(target, path)?.seconds) {
            const timestamp = _.get(target, path);
            value = value.replace(
              token[0],
              moment(timestamp.toDate()).format()
            );
          } else if (underlyingField?.type === 'image') {
            value = _.get(target, path);
          } else {
            const targetResult = _.get(target, path);
            if (Array.isArray(targetResult)) {
              value = [...targetResult];
            } else if (
              Object.prototype.toString.call(targetResult) === '[object Object]'
            ) {
              value = { ...targetResult };
            } else {
              value = value.replace(token[0], targetResult);
            }
          }
        }

        if (/^\d+$/.test(value)) {
          value = parseFloat(value);
        }
      });
    }
  }

  if (underlyingField?.type === 'image') {
    return value;
  }

  if (value === 'true' || value === true) {
    return options?.booleanToYesNo ? 'Yes' : true;
  } else if (value === 'false' || value === false) {
    return options?.booleanToYesNo ? 'No' : false;
  }

  if (value?.indexOf && value.indexOf('if(') === 0) {
    value = value.replace('if(', '');
    try {
      value = value.substring(0, value.length - 1);
      // eslint-disable-next-line no-eval
      value = eval(value);
    } catch (err) {
      // na
    }
  }

  value = formatDate(value);

  return value;
};

/*  
  returns a single function for parsing anything from firestore paths to field values

  "markup" can be made up of one or more of the following:

  - a plain value i.e. ascii character 
  - an object path contained in {} prefixed with one of user, local, ds or datasource
  
  Legacy support for ${source.
    
    if the path starts with ${source. we remove the $ and handle it as {source.field}
    providing an individual "document" parameter has been supplied. If a document has not been 
    supplied we replace {source.field} with {ds.datasoureId.field} by looking at the
    element.datasourceId

  "element" legacy support for ${source.whatever} where the element has a datasource or datasourceId
    such that we re-create the markup as {ds.datasourceId.whatever}
    
  "options" provides additional options 
    i.e. { booleanToYesNo: true }

  Special handling:

  - "true" "false" as strings are converted to real true/false
  - <br/> are replaced
  - strings that contain all numbers are converted to floats by /^\d+$/.test(text)
  - if the value is a firebase timestamp it is formatted to a string
  - 
  
  currently suppored markup functions, applied after interpolation

  - formatDate(theDate,'a momentjs formatting string')
  - if(condition ? if_true : if_false)

  returns
  {
    value: the final value,
    lines: the final value split into an array by <br/>
  }
  
*/
const useParse = () => {
  const { user: currentUser } = useSelector(state => state.User);
  const localStore = useSelector(state => state.Local);
  const {
    dataLinks,
    selections: dataLinkSelections,
    documents: allDocuments
  } = useSelector(state => state.DataLinks);

  /* make it possible to only have to supply the markup 
     we make it possible to supply the other values to support async action sequencing where
     we do not see redux changes that are dispatched during a sequencer, so instead, we pass 
     the values around at the start of the sequence
  */
  const internalParse = ({
    markup,
    element,
    document,
    documents = allDocuments,
    selections = dataLinkSelections,
    local = localStore,
    user = currentUser,
    options,
    data,
    actionSequenceExecutionHistory
  }) => {
    let targetMarkup = markup;
    if (targetMarkup?.indexOf && targetMarkup.indexOf('${source.') > -1) {
      if (
        element?.datasourceId?.trim().length > 0 ||
        element?.datasource?.trim().length > 0
      ) {
        targetMarkup = targetMarkup.replace(
          /\${source./g,
          `{ds.${element?.datasourceId || element?.datasource}.`
        );
      } else {
        targetMarkup = targetMarkup.replace(/\${source./g, '{source.');
      }
    }

    const result = parse({
      markup: targetMarkup,
      actionSequenceExecutionHistory,
      documents,
      document,
      selections,
      local,
      user,
      options,
      data,
      dataLinks
    });

    return result;
  };

  return { parse: internalParse };
};

export default useParse;
